import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import {
  Plus, Pencil, Trash2, Search, X, Loader2, ChevronDown, Filter, Upload, Wrench, ClipboardCheck
} from "lucide-react";
import PageHeader from "../components/PageHeader";

import {
  TABS, STATUT_OPTIONS, STATUT_LABELS, FIELDS, TABLE_COLS,
  COL_LABELS, FILTER_TYPE_OPTIONS
} from "./outilsConstants";

import {
  StatusBadge, CardBrandBadge, FormField, EcheanceBanner,
  OutilsDashboard, InvoiceWidget, MaintenancePanel, InspectionsPanel
} from "./outilsComponents";

export default function OutilsPage() {
  const { axiosAuth } = useAuth();
  const { isDark } = useTheme();
  const [tab, setTab] = useState("vehicules");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [expandedLocal, setExpandedLocal] = useState(null);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [expandedEquipement, setExpandedEquipement] = useState(null);
  const [echeances, setEcheances] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [stats, setStats] = useState({});
  const [vehicleLookup, setVehicleLookup] = useState([]);
  const [personneLookup, setPersonneLookup] = useState([]);
  const [filterStatut, setFilterStatut] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSociete, setFilterSociete] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const params = {};
      if (filterStatut) params.statut = filterStatut;
      if (filterSociete && ["vehicules","paiements","carburant","telepeage"].includes(tab)) params.societe = filterSociete;
      if (filterType) params.type = filterType;
      const res = await api.get(`/outils/${tab}`, { params });
      setItems(res.data);
    } catch { setItems([]); }
    setLoading(false);
  }, [tab, axiosAuth, filterStatut, filterType, filterSociete]);

  const fetchEcheances = useCallback(async () => {
    try { const res = await axiosAuth().get("/outils/echeances", { params: { days: 60 } }); setEcheances(res.data); } catch { setEcheances([]); }
  }, [axiosAuth]);

  const fetchStats = useCallback(async () => {
    try { const res = await axiosAuth().get("/outils/stats/counts"); setStats(res.data); } catch { setStats({}); }
  }, [axiosAuth]);

  const fetchLookups = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [vRes, pRes] = await Promise.all([api.get("/outils/lookup/vehicules"), api.get("/outils/lookup/personnes")]);
      setVehicleLookup(vRes.data);
      setPersonneLookup(pRes.data);
    } catch {}
  }, [axiosAuth]);

  useEffect(() => { fetchItems(); setSearch(""); }, [fetchItems]);
  useEffect(() => { fetchEcheances(); fetchLookups(); fetchStats(); }, [fetchEcheances, fetchLookups, fetchStats]);

  const handleRefreshRappels = async () => {
    try { const res = await axiosAuth().post("/outils/rappels/check", { anticipation_days: [30, 15, 7] }); alert(res.data.message); fetchEcheances(); } catch {}
  };

  const resetFilters = () => { setFilterStatut(""); setFilterType(""); setFilterSociete(""); };
  const hasActiveFilters = filterStatut || filterType || filterSociete;

  const openCreate = () => {
    setEditItem(null);
    const init = {};
    FIELDS[tab].forEach(f => { init[f.key] = f.key === "statut" ? "actif" : ""; });
    setForm(init);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    const init = {};
    FIELDS[tab].forEach(f => { init[f.key] = item[f.key] ?? ""; });
    setForm(init);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const api = axiosAuth();
      const payload = { ...form };
      if (payload.vehicule_id && (tab === "carburant" || tab === "telepeage")) {
        const veh = vehicleLookup.find(v => v.value === payload.vehicule_id);
        payload.vehicule_label = veh ? veh.label : "";
      }
      if (payload.personne_affectee && (tab === "carburant" || tab === "telepeage")) {
        const pers = personneLookup.find(p => p.value === payload.personne_affectee);
        if (pers) payload.personne_affectee = pers.label;
      }
      if (editItem) await api.put(`/outils/${tab}/${editItem.item_id}`, payload);
      else await api.post(`/outils/${tab}`, payload);
      setShowModal(false);
      fetchItems();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const handleDelete = async (itemId) => {
    try { await axiosAuth().delete(`/outils/${tab}/${itemId}`); setDeleteId(null); if (expandedLocal === itemId) setExpandedLocal(null); fetchItems(); } catch {}
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const api = axiosAuth();
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/outils/import/${tab}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extracted = res.data.extracted || {};
      const init = {};
      FIELDS[tab].forEach(f => {
        init[f.key] = extracted[f.key] ?? (f.key === "statut" ? "actif" : "");
      });
      setEditItem(null);
      setForm(init);
      setShowModal(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'import");
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const filtered = items.filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return Object.values(item).some(v => String(v).toLowerCase().includes(s));
  });

  const cols = TABLE_COLS[tab];
  const tabInfo = TABS.find(t => t.key === tab);
  const selectCls = isDark ? "bg-[#0b0811] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900";

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: "var(--bg-primary)", color: "var(--text-primary)" }} data-testid="outils-page">
      <PageHeader
        icon={<Wrench />}
        title="Équipements"
        description="Gestion des ressources internes"
        testId="outils-header"
      />

      <EcheanceBanner echeances={echeances} onRefresh={handleRefreshRappels} />
      <OutilsDashboard stats={stats} echeances={echeances} />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 p-1 rounded-xl mb-6" style={{ background: "var(--bg-card)" }} data-testid="outils-tabs">
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); resetFilters(); setExpandedLocal(null); setExpandedVehicle(null); setExpandedEquipement(null); }} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${active ? "shadow-sm" : "hover:opacity-80"}`}
              style={active ? { background: "var(--accent)", color: "#14101c" } : { color: "var(--text-muted)" }}>
              <t.icon className="w-4 h-4" /><span className="hidden sm:inline">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-dimmed)" }} />
            <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} data-testid="outils-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm border focus:outline-none focus:ring-1 focus:ring-[var(--accent)]"
              style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)", color: "var(--text-primary)" }} />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle"
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm border transition-colors ${hasActiveFilters ? "border-[var(--accent)]" : ""}`}
            style={{ background: "var(--bg-card)", borderColor: hasActiveFilters ? "var(--accent)" : "var(--border-primary)", color: hasActiveFilters ? "var(--accent)" : "var(--text-muted)" }}>
            <Filter className="w-4 h-4" /><span className="hidden sm:inline">Filtres</span>{hasActiveFilters && <span className="w-2 h-2 rounded-full bg-[var(--accent)]" />}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <input type="file" ref={fileRef} onChange={handleImportFile} accept=".pdf,.docx,.xlsx,.csv" className="hidden" data-testid="import-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="outils-import-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50"
            style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)", background: "var(--bg-card)" }}>
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            <span className="hidden sm:inline">Importer</span>
          </button>
          <button onClick={openCreate} data-testid="outils-add-btn" className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold" style={{ background: "var(--accent)", color: "#14101c" }}>
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 mb-4 p-3 rounded-xl border" style={{ background: "var(--bg-card)", borderColor: "var(--border-secondary)" }} data-testid="filter-bar">
          <div className="relative">
            <select className={`text-xs rounded-lg px-3 py-2 border appearance-none pr-7 ${selectCls}`} value={filterStatut} onChange={e => setFilterStatut(e.target.value)} data-testid="filter-statut" style={{ borderColor: "var(--border-primary)" }}>
              <option value="">Tous les statuts</option>{STATUT_OPTIONS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
            </select><ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-50" />
          </div>
          {FILTER_TYPE_OPTIONS[tab] && (
            <div className="relative">
              <select className={`text-xs rounded-lg px-3 py-2 border appearance-none pr-7 ${selectCls}`} value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="filter-type" style={{ borderColor: "var(--border-primary)" }}>
                <option value="">Tous les types</option>{FILTER_TYPE_OPTIONS[tab].map(t => <option key={t} value={t}>{t}</option>)}
              </select><ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none opacity-50" />
            </div>
          )}
          {["vehicules","paiements","carburant","telepeage"].includes(tab) && (
            <input type="text" placeholder="Societe..." value={filterSociete} onChange={e => setFilterSociete(e.target.value)} data-testid="filter-societe"
              className={`text-xs rounded-lg px-3 py-2 border w-36 ${selectCls}`} style={{ borderColor: "var(--border-primary)" }} />
          )}
          {hasActiveFilters && <button onClick={resetFilters} className="text-xs px-2.5 py-1.5 rounded-lg text-red-400 hover:bg-red-500/10" data-testid="filter-reset">Reinitialiser</button>}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }}>
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="outils-loading"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--accent)" }} /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="outils-empty">
            <tabInfo.icon className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--text-dimmed)" }} />
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>{search || hasActiveFilters ? "Aucun resultat" : `Aucun ${tabInfo.label.toLowerCase()} enregistre`}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="outils-table">
              <thead>
                <tr style={{ borderBottom: `1px solid var(--border-primary)` }}>
                  {(tab === "locaux" || tab === "vehicules" || tab === "equipements") && <th className="w-10" />}
                  {cols.map(c => <th key={c} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-dimmed)" }}>{COL_LABELS[c] || c}</th>)}
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-dimmed)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <Fragment key={item.item_id}>
                    <tr className="transition-colors hover:brightness-110" style={{ borderBottom: `1px solid var(--border-secondary)` }} data-testid={`row-${item.item_id}`}>
                      {tab === "locaux" && (
                        <td className="pl-3">
                          <button onClick={() => setExpandedLocal(expandedLocal === item.item_id ? null : item.item_id)}
                            className={`p-1.5 rounded-lg transition-colors ${expandedLocal === item.item_id ? "bg-[var(--accent)]/10" : "hover:bg-white/5"}`}
                            data-testid={`expand-${item.item_id}`} title="Voir inspections">
                            <ClipboardCheck className="w-4 h-4" style={{ color: expandedLocal === item.item_id ? "var(--accent)" : "var(--text-dimmed)" }} />
                          </button>
                        </td>
                      )}
                      {tab === "vehicules" && (
                        <td className="pl-3">
                          <button onClick={() => setExpandedVehicle(expandedVehicle === item.item_id ? null : item.item_id)}
                            className={`p-1.5 rounded-lg transition-colors ${expandedVehicle === item.item_id ? "bg-[var(--accent)]/10" : "hover:bg-white/5"}`}
                            data-testid={`expand-veh-${item.item_id}`} title="Voir maintenance">
                            <Wrench className="w-4 h-4" style={{ color: expandedVehicle === item.item_id ? "var(--accent)" : "var(--text-dimmed)" }} />
                          </button>
                        </td>
                      )}
                      {tab === "equipements" && (
                        <td className="pl-3">
                          <button onClick={() => setExpandedEquipement(expandedEquipement === item.item_id ? null : item.item_id)}
                            className={`p-1.5 rounded-lg transition-colors ${expandedEquipement === item.item_id ? "bg-[var(--accent)]/10" : "hover:bg-white/5"}`}
                            data-testid={`expand-eqp-${item.item_id}`} title="Voir factures">
                            <Upload className="w-4 h-4" style={{ color: expandedEquipement === item.item_id ? "var(--accent)" : "var(--text-dimmed)" }} />
                          </button>
                        </td>
                      )}
                      {cols.map(c => (
                        <td key={c} className="px-4 py-3" style={{ color: "var(--text-secondary)" }}>
                          {c === "statut" ? <StatusBadge value={item[c]} />
                          : c === "type_paiement" ? <CardBrandBadge type={item[c]} />
                          : c === "km" ? (item[c] ? Number(item[c]).toLocaleString("fr-FR") : "\u2014")
                          : c === "loyer_mensuel" ? (item[c] ? `${Number(item[c]).toLocaleString("fr-FR")} EUR` : "\u2014")
                          : c === "surface_m2" ? (item[c] ? `${item[c]} m2` : "\u2014")
                          : (item[c] || "\u2014")}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(item)} data-testid={`edit-${item.item_id}`} className="p-2 rounded-lg hover:bg-blue-500/10" style={{ color: "var(--text-muted)" }}><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => setDeleteId(item.item_id)} data-testid={`delete-${item.item_id}`} className="p-2 rounded-lg hover:bg-red-500/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                    {tab === "locaux" && expandedLocal === item.item_id && (
                      <tr key={`insp-${item.item_id}`}><td colSpan={cols.length + 2} className="px-4 py-4" style={{ background: "var(--bg-card-alt)" }}>
                        <InspectionsPanel localId={item.item_id} localNom={item.nom} axiosAuth={axiosAuth} isDark={isDark} />
                      </td></tr>
                    )}
                    {tab === "vehicules" && expandedVehicle === item.item_id && (
                      <tr key={`maint-${item.item_id}`}><td colSpan={cols.length + 2} className="px-4 py-4" style={{ background: "var(--bg-card-alt)" }}>
                        <MaintenancePanel vehiculeId={item.item_id} vehiculeLabel={`${item.marque||""} ${item.modele||""} - ${item.immatriculation||""}`} axiosAuth={axiosAuth} isDark={isDark} />
                      </td></tr>
                    )}
                    {tab === "equipements" && expandedEquipement === item.item_id && (
                      <tr key={`inv-eqp-${item.item_id}`}><td colSpan={cols.length + 2} className="px-4 py-4" style={{ background: "var(--bg-card-alt)" }}>
                        <div data-testid={`eqp-invoices-panel-${item.item_id}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <Upload className="w-4 h-4" style={{ color: "var(--accent)" }} />
                            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>Factures — {item.marque || ""} {item.modele || ""}</span>
                          </div>
                          <InvoiceWidget entityType="equipement" entityId={item.item_id} axiosAuth={axiosAuth} />
                        </div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs mt-3" style={{ color: "var(--text-dimmed)" }} data-testid="outils-count">{filtered.length} {tabInfo.label.toLowerCase()}</p>

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => setShowModal(false)} data-testid="outils-modal-backdrop">
          <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-xl border p-6" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} onClick={e => e.stopPropagation()} data-testid="outils-modal">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold" style={{ color: "var(--accent)" }} data-testid="modal-title">{editItem ? `Modifier ${tabInfo.label}` : `Nouveau ${tabInfo.label}`}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="modal-close"><X className="w-5 h-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-4">
              {FIELDS[tab].map(field => (
                <div key={field.key}>
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: "var(--text-muted)" }}>{field.label}{field.required && <span className="text-red-400 ml-0.5">*</span>}</label>
                  <FormField field={field} value={form[field.key]} onChange={val => setForm({ ...form, [field.key]: val })} isDark={isDark} vehicleLookup={vehicleLookup} personneLookup={personneLookup} />
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 rounded-lg text-sm border" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }} data-testid="modal-cancel">Annuler</button>
              <button onClick={handleSave} disabled={saving} data-testid="modal-save" className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-50" style={{ background: "var(--accent)", color: "#14101c" }}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : editItem ? "Enregistrer" : "Creer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" onClick={() => setDeleteId(null)} data-testid="delete-confirm-backdrop">
          <div className="w-full max-w-sm rounded-xl border p-6" style={{ background: "var(--bg-card)", borderColor: "var(--border-primary)" }} onClick={e => e.stopPropagation()} data-testid="delete-confirm">
            <h3 className="text-base font-bold mb-2" style={{ color: "var(--text-primary)" }}>Supprimer ?</h3>
            <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>Cette action est irreversible.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-2.5 rounded-lg text-sm border" style={{ borderColor: "var(--border-primary)", color: "var(--text-muted)" }} data-testid="delete-cancel">Annuler</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 px-4 py-2.5 rounded-lg text-sm font-bold bg-red-500 text-white hover:bg-red-600" data-testid="delete-confirm-btn">Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
